import React, { useState } from 'react';
import { useVerifyUserEmail } from '../../api/users';
import { useLoginModalContext } from '../../context/LoginModalContext';
import { useUserContext } from '../../context/UserContext';
import { useNotifications } from '../../context/NotificationContext';
import { X, Sun } from 'lucide-react';
import './LoginModal.css';

interface LoginModalEmailProps {
  onClose: () => void;
}

const LoginModalEmail: React.FC<LoginModalEmailProps> = ({ 
  onClose
}) => {
  const [email, setEmail] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [isGoogleLoading, setIsGoogleLoading] = useState(false);
  const verifyUserMutation = useVerifyUserEmail();
  const { setCurrentStep, setEmail: setContextEmail } = useLoginModalContext();
  const { signInWithGoogle } = useUserContext();
  const { showSuccess, showError } = useNotifications();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrorMessage('');
    
    if (email.trim()) {
      try {
        const result = await verifyUserMutation.mutateAsync({ 
          email: email.trim(), 
        });

        setContextEmail(email.trim());
        if (result.is_verified) {
          setCurrentStep('password');
        } else {
          setCurrentStep('verification');
        }
      } catch (error: any) {
        console.error('Login failed:', error);
        
        if (error?.response?.status === 404) {
          setErrorMessage('No user found with this email. Please register.');
        } else if (error?.message) {
          setErrorMessage(error.message);
        } else {
          setErrorMessage('Login failed. Please try again.');
        }
      }
    }
  };

  const handleGoogleLogin = async () => {
    setIsGoogleLoading(true);
    try {
      await signInWithGoogle();
      onClose();
    } catch (error: any) {
      console.error('Google login error:', error);
      showError('Failed to sign in with Google. Please try again.', error);
    } finally {
      setIsGoogleLoading(false);
    }
  };

  return (
    <>
      <div className="modal-overlay" onClick={onClose}>
        <div className="modal-content" onClick={(e) => e.stopPropagation()}>
          <button 
            className="modal-close-btn" 
            onClick={onClose}
            aria-label="Close modal"
          >
            <X size={20} />
          </button>

          <h1 className="modal-title">Welcome Back!</h1>
          
          <p className="modal-description">
            Sign in to your account to access your saved camps and schedules.
          </p>
          
          {errorMessage && (
            <div className="message message--error">
              {errorMessage}
            </div>
          )}
          
          <form onSubmit={handleSubmit} className="login-form">
            <div className="form-group">
              <label htmlFor="email">Email</label>
              <input
                type="email"
                id="email"
                className="form-input"
                value={email}
                onChange={(e) => {
                  setEmail(e.target.value);
                  if (errorMessage) setErrorMessage('');
                }}
                placeholder="hello@example.com"
                required
                disabled={verifyUserMutation.isPending}
              />
            </div>
            
            <button 
              type="submit" 
              className="btn btn--primary btn--login"
              disabled={verifyUserMutation.isPending}
            >
              Continue
            </button>
          </form>

          <div className="login-divider">
            <span>or</span>
          </div>

          <button 
            type="button" 
            className="btn btn--google"
            onClick={handleGoogleLogin}
            disabled={isGoogleLoading}
          >
            <svg width="20" height="20" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
              <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/>
              <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
              <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
              <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
            </svg>
            {isGoogleLoading ? 'Signing in...' : 'Continue with Google'}
          </button>

          <div className="button-group">
            <p className="login-link">
              <button 
                type="button" 
                className="login-link-text" 
                onClick={() => setCurrentStep('reset-password')}
              >
                Forgot your password?
              </button>
            </p>

            <p className="login-link">
              Don't have an account? <button 
                type="button" 
                className="login-link-text" 
                onClick={() => setCurrentStep('register')}
              >
                Register
              </button>
            </p>
          </div>
        </div>
      </div>
    </>
  );
};

export default LoginModalEmail;
